import { defineStore } from "pinia";

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,
        roles: [],
        lastRole: null,
        isLoading: false,
        isAuthenticated: false,
        isChecking: true,
        // MFA session state
        mfaSessionId: null,
        mfaAvailableMethods: [],
        mfaSelectedMethod: null,
    }),

    getters: {
        getUser: (state) => state.user,
        getUserName: (state) => state.user?.userName,
        getUserRoles: (state) => state.roles,
        getLastRole: (state) => state.lastRole,
        getIsLoading: (state) => state.isLoading,

        // Dynamically calculate `isAuthenticated` based on token and its expiration
        getIsAuthenticated: (state) => state.isAuthenticated,
        getIsChecking: (state) => state.isChecking,
    },

    actions: {
        setLoginData(loginResponse) {
            this.user = loginResponse.userData;
            this.roles = loginResponse.roles;
            this.lastRole = loginResponse.lastRole;
            this.isAuthenticated = true;
            this.isChecking = false;
            // Store minimal auth info in localStorage for persistence
            localStorage.setItem('auth_lastRole', this.lastRole);
            localStorage.setItem('auth_timestamp', Date.now().toString());
        },

        setLoading(status) {
            this.isLoading = status;
        },


        logout() {
            this.user = null;
            this.roles = [];
            this.lastRole = null;
            this.isAuthenticated = false;
            this.isChecking = false;

            // Clear MFA session state
            this.mfaSessionId = null;
            this.mfaAvailableMethods = [];
            this.mfaSelectedMethod = null;

            // Clear localStorage
            localStorage.removeItem('auth_lastRole');
            localStorage.removeItem('auth_timestamp');
        },

        initializeStore(user, lastRole, roles, isAuthenticated) {
            this.user = user;
            this.lastRole = lastRole;
            this.roles = roles;
            this.isAuthenticated = isAuthenticated;
            this.isChecking = false;

            // Store minimal auth info in localStorage
            if (isAuthenticated) {
                localStorage.setItem('auth_lastRole', lastRole);
                localStorage.setItem('auth_timestamp', Date.now().toString());
            }
        },
        // Start authentication check - reset data to prevent stale display
        startAuthCheck() {
            this.isChecking = true;
            // Reset user data and lastRole to prevent stale display during check
            this.user = null;
            this.lastRole = null;
            this.isAuthenticated = false;
        },
        // Complete authentication check
        completeAuthCheck() {
            this.isChecking = false;
        },

        // Wait for authentication check to complete
        async waitForAuthCheck() {
            if (!this.isChecking) {
                return;
            }

            return new Promise((resolve) => {
                const unwatch = this.$subscribe((mutation, state) => {
                    if (!state.isChecking) {
                        unwatch();
                        resolve();
                    }
                });
            });
        },
        // Initialize from localStorage (for faster initial state)
        initializeFromStorage() {
            const lastRole = localStorage.getItem('auth_lastRole');
            const timestamp = localStorage.getItem('auth_timestamp');

            if (lastRole && timestamp) {
                // Check if the stored data is not too old (e.g., 24 hours)
                const isRecent = (Date.now() - parseInt(timestamp)) < (24 * 60 * 60 * 1000);
                if (isRecent) {
                    // Don't set lastRole here, let the actual API check set it
                    // This prevents stale data from showing during authentication check
                }
            }

            // Ensure isChecking starts as true to prevent race conditions
            this.isChecking = true;
        },

        // MFA-specific actions
        setMfaSession(sessionId, availableMethods) {
            this.mfaSessionId = sessionId;
            this.mfaAvailableMethods = availableMethods;
        },

        setMfaSelectedMethod(method) {
            this.mfaSelectedMethod = method;
        },

        clearMfaSession() {
            this.mfaSessionId = null;
            this.mfaAvailableMethods = [];
            this.mfaSelectedMethod = null;
        }
    }
});
